import { useState, useEffect, useMemo } from 'react';
import { Header, Button, Label } from 'semantic-ui-react';
import { Link as ReactRouterLink } from 'react-router-dom';
import ReactGA from 'react-ga4';

import { useProjectContext } from '~/hooks';
import { DealStates } from '~/constants';
import { ProjectBidSummary } from '~/types/project';
import ProjectBidTable from './ProjectBidTable';
import ProjectBidCards from './ProjectBidCards';
import { usePermissions } from '~/requests/permissions/usePermissions';
import AdminControlPanel from '../../admin/AdminControlPanel';
import InvestorBidArea from '../investor';
import RfiTable from '../../requestForInformation/admin/RfiTable';
import RfiQuestionnaire from '../../requestForInformation/investor/RfiQuestionnaire';
import DealExclusionCard from '~/components/DealExclusionCard';
import BuyerProfileMatchPanel from '~/features/buyerProfile/BuyerProfileMatchPanel';
import '../styles.scss';

export default function ProjectBidding() {
  const {
    project,
    projectDisplayValues
  } = useProjectContext();

  const { dealSummary, investorBidArea } = projectDisplayValues;
  const { bidDeadline } = investorBidArea || {};

  const [sortedBidData, setSortedBidData] = useState<ProjectBidSummary[]>([]);
  const [showBidSummary, setShowBidSummary] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(() => {
    const savedViewMode = localStorage.getItem('biddingViewMode');
    if (project?.pricingType === 'OFFTAKE') {
      return 'cards';
    }
    return (savedViewMode as 'table' | 'cards') || 'cards';
  });
  const [budgetDisplayMode, setBudgetDisplayMode] = useState<'unit' | 'total'>(() => {
    const saved = localStorage.getItem('projectBudgetDisplayMode');
    if (project?.pricingType === 'OFFTAKE') {
      return 'unit';
    }
    return (saved as 'unit' | 'total') || 'unit';
  });
  const [showDeclined, setShowDeclined] = useState<boolean>(() => {
    return localStorage.getItem('projectBiddingShowDeclined') !== 'false';
  });
  const { permissions } = usePermissions();

  ReactGA.send({
    hitType: 'pageview',
    page: '/project/id/bidding',
    title: `${project?.name} | Conductor Solar`
  });
  document.title = `${project?.name} | Conductor Solar`;

  useEffect(() => {
    sortBidData();
  }, [project]);

  useEffect(() => {
    if (sortedBidData?.length > 0) {
      setShowBidSummary(true);
    } else {
      setShowBidSummary(false);
    }
  }, [sortedBidData]);



  const handleViewModeToggle = (mode: 'table' | 'cards') => {
    setViewMode(mode);
    localStorage.setItem('biddingViewMode', mode);
  };

  const handleBudgetDisplayModeToggle = (mode: 'unit' | 'total') => {
    setBudgetDisplayMode(mode);
    localStorage.setItem('projectBudgetDisplayMode', mode);
  };

  const handleShowDeclinedToggle = (show: boolean) => {
    setShowDeclined(show);
    localStorage.setItem('projectBiddingShowDeclined', String(show));
  };

  const declinedCount = useMemo(
    () => sortedBidData?.filter((d) => d.projectStatus === 'DECLINED_TO_BID' || d.projectStatus === 'BID_REJECTED').length ?? 0,
    [sortedBidData]
  );

  const visibleBidData = useMemo(
    () => showDeclined
      ? sortedBidData
      : sortedBidData?.filter((d) => d.projectStatus !== 'DECLINED_TO_BID' && d.projectStatus !== 'BID_REJECTED'),
    [sortedBidData, showDeclined]
  );

  const sortBidData = () => {
    const isDeclined = (d: ProjectBidSummary) =>
      d.projectStatus === 'DECLINED_TO_BID' || d.projectStatus === 'BID_REJECTED';
    const currentBidData = project?.bidSummary
      ?.sort((a: ProjectBidSummary, b: ProjectBidSummary) => {
        const aDeclined = isDeclined(a);
        const bDeclined = isDeclined(b);
        if (aDeclined !== bDeclined) return aDeclined ? 1 : -1;
        if (!a.bid && b.bid) {
          return 1;
        }
        if (a.bid && !b.bid) {
          return -1;
        }
        if (a.bid && b.bid) {
          return (b.bid.amount || 0) - (a.bid.amount || 0);
        }
        return 0;
      }) as ProjectBidSummary[];
    setSortedBidData(currentBidData);
  };

  return (
    <>

      {(permissions?.isAdmin || permissions?.isDeveloper) && (
        <>
          {showBidSummary && (
            <div className="card">

              <div className="deal-summary-header">
                <Header as="h2">
                  {dealSummary?.headerText}
                  {Boolean(bidDeadline?.bidDeadlineData) && (
                    <Label color='olive'>
                      Bidding
                    </Label>
                  )}
                </Header>
                {sortedBidData?.length > 0 && (permissions.isAdmin || (permissions.isDeveloper && project.pricingType !== 'OFFTAKE')) ? (

                  <div>
                    <p>
                      You have {sortedBidData?.length} investor{sortedBidData?.length !== 1 ? 's' : ''} bidding on your project.
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                      <div className="view-mode-toggle" style={{ paddingBottom: '0', paddingTop: '12px' }}>
                        <span>View mode: </span>
                        <Button.Group>
                          <Button
                            size="small"
                            basic
                            active={viewMode === 'cards'}
                            onClick={() => handleViewModeToggle('cards')}
                          >
                            Cards
                          </Button>
                          <Button
                            size="small"
                            basic
                            active={viewMode === 'table'}
                            onClick={() => handleViewModeToggle('table')}
                          >
                            Table
                          </Button>
                        </Button.Group>
                      </div>

                      {(viewMode === 'cards' && project.pricingType !== 'OFFTAKE') && (
                        <div className="view-mode-toggle" style={{ paddingBottom: '0', paddingTop: '12px' }}>
                          <span>Budget display: </span>
                          <Button.Group>
                            <Button
                              size="small"
                              basic
                              active={budgetDisplayMode === 'unit'}
                              onClick={() => handleBudgetDisplayModeToggle('unit')}
                            >
                              Unit
                            </Button>
                            <Button
                              size="small"
                              basic
                              active={budgetDisplayMode === 'total'}
                              onClick={() => handleBudgetDisplayModeToggle('total')}
                            >
                              Total
                            </Button>
                          </Button.Group>
                        </div>
                      )}

                      {declinedCount > 0 && (
                        <div className="view-mode-toggle" style={{ paddingBottom: '0', paddingTop: '12px' }}>
                          <span>Investor list: </span>
                          <Button.Group>
                            <Button
                              size="small"
                              basic
                              active={!showDeclined}
                              onClick={() => handleShowDeclinedToggle(false)}
                            >
                              Active
                            </Button>
                            <Button
                              size="small"
                              basic
                              active={showDeclined}
                              onClick={() => handleShowDeclinedToggle(true)}
                            >
                              All
                            </Button>
                          </Button.Group>
                        </div>
                      )}
                    </div>
                  </div>

                ) : null}

              </div>



              {viewMode === 'table' ? (
                <ProjectBidTable sortedBidData={visibleBidData} />
              ) : (
                <ProjectBidCards sortedBidData={visibleBidData} budgetDisplayMode={budgetDisplayMode} />
              )}

            </div>
          )}

          {!showBidSummary && (
            <div className="deal-summary-empty-state card">
              <Header as="h2"><i className="hourglass half icon"></i>Matching with investors...</Header>
              <Header as="h4">You will receive an email notification once investors have been invited to bid.</Header>
              <p>
                Here&rsquo;s what&rsquo;s happening:
                <ol>
                  <li>Your project is being reviewed for completeness, to ensure it will attract optimal bids.</li>
                  <li>
                    Conductor uses the project information that you&rsquo;ve entered to identify the best investors. We evaluate your project&rsquo;s alignment with over 100 investors across market fit, deal fit, project fit, partner fit, and recent performance. This surfaces the investors most likely to bid aggressively and follow-through on their deal terms.
                  </li>
                  <li>
                    When Conductor matches your project with an investor and invites them to bid, you&rsquo;ll receive an email notification.
                  </li>
                </ol>
              </p>
            </div>
          )}
        </>
      )}

      {(permissions?.isInvestor && !project.portfolioId) && (
        <InvestorBidArea />
      )}

      {(permissions?.isInvestor && !project.portfolioId) && (
        <RfiQuestionnaire />
      )}

      {(permissions?.isInvestor && project.portfolioId) && (
        <div className="card">
          <Header as='h2'>Bidding for this project is handled at the portfolio level.</Header>
          <p style={{ marginBottom: '20px' }}>Manage your bids and supporting information on bidding tab of the portfolio page.</p>
          <Button
            size="small"
            as={ReactRouterLink}
            to={`/portfolio/${project.portfolioId}/bidding`}
            primary
          >
            Go there now
          </Button>
        </div>
      )}

      {permissions?.isAdmin && <AdminControlPanel />}
      {permissions?.isAdmin && <RfiTable />}
      {permissions?.isAdmin && (
        <DealExclusionCard projectId={project?.id} />
      )}
      {permissions?.isAdmin && (
        <BuyerProfileMatchPanel projectId={project?.id} />
      )}

      {(!permissions?.isAdmin && (project.dealState >= DealStates.IN_DILIGENCE || project.dealState === DealStates.DECLINED)) && (
        <>
          {window.location.assign(`/project/${project.id}/review`)}
        </>
      )}

    </>

  );
}  